import { useAppDispatch } from '@/hooks';
import { toggleEditStake } from '@/store';
import { useLogoutMutation } from '@/store/api/authApi';
import { setRulesModalOpen } from '@/store/slices/uiSlice';
import { Icons } from '@/utils/Icons';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useNavigate } from 'react-router-dom';

type UserDropdownProps = {
  isDemoUser: boolean;
  username: string;
  showBalance: boolean;
  showExposure: boolean;
  handleBalanceChange: (value: boolean) => void;
  handleExposureChange: (value: boolean) => void;
};
const UserDropdown: React.FC<UserDropdownProps> = ({
  isDemoUser,
  username,
  handleBalanceChange,
  handleExposureChange,
  showBalance,
  showExposure,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [logout] = useLogoutMutation();

  const selectItems = [
    {
      label: 'Account Statement',
      path: '/user/account-statement',
    },
    {
      label: 'Bet History',
      path: '/user/bet-history',
    },
    {
      label: 'Activity Logs',
      path: '/user/activity-logs',
      protected: true,
    },
    {
      label: 'Casino Bet History',
      path: '/user/casino-bet-history',
      protected: true,
    },
    {
      label: 'Deposit',
      path: '/deposit',
      protected: true,
    },
    {
      label: 'Withdraw',
      path: '/withdraw',
      protected: true,
    },
  ];

  const itemClass =
    'px-3 py-2 text-base text-black rounded-sm cursor-pointer outline-none hover:underline hover:bg-gray-100 focus:bg-gray-100 data-[highlighted]:bg-gray-100';

const handleLogout = async () => {
  try {
    await logout().unwrap();
  } catch {
    // The base query handles session cleanup and API errors.
  }
};

return (
  <DropdownMenu.Root>
    <DropdownMenu.Trigger asChild>
      <button className="flex items-center gap-1 text-white text-xs md:text-base rounded-sm transition focus:outline-none">
        {isDemoUser ? 'Demo' : username}
        <Icons.Down className="text-xs md:text-base" />
      </button>
    </DropdownMenu.Trigger>

    <DropdownMenu.Portal>
      <DropdownMenu.Content
        align="end"
        sideOffset={5}
        className="min-w-[200px] bg-white text-gray-800 rounded-md shadow-lg border p-1 z-50"
      >
        {/* Dynamic navigation items */}
        {selectItems.map((item) => {
          if (item?.protected && isDemoUser) return;
          return (
            <DropdownMenu.Item
              key={item.path}
              className={itemClass}
              onSelect={() => navigate(item.path)}
            >
              {item.label}
            </DropdownMenu.Item>
          );
        })}

        {/* Redux actions */}
        <DropdownMenu.Item className={itemClass} onSelect={() => dispatch(toggleEditStake())}>
          Set Button Values
        </DropdownMenu.Item>

        {!isDemoUser && (
          <>
            {' '}
            <DropdownMenu.Item
              className={itemClass}
              onSelect={() => navigate('/user/secure-auth-verification')}
            >
              Security Auth Verification
            </DropdownMenu.Item>
            <DropdownMenu.Item
              className={itemClass}
              onSelect={() => navigate('/user/change-password')}
            >
              Change Password
            </DropdownMenu.Item>
          </>
        )}
        <DropdownMenu.Item
          className={`${itemClass} xl:hidden`}
          onSelect={() => dispatch(setRulesModalOpen(true))}
        >
          Rules
        </DropdownMenu.Item>
        <DropdownMenu.Item
          className={`${itemClass} xl:hidden`}
          onSelect={(e) => {
            e.preventDefault();
            handleBalanceChange(!showBalance);
          }}
        >
          <div className="flex items-center justify-between">
            Balance <input type="checkbox" checked={showBalance} readOnly />
          </div>
        </DropdownMenu.Item>

        <DropdownMenu.Item
          className={`${itemClass} xl:hidden`}
          onSelect={(e) => {
            e.preventDefault();
            handleExposureChange(!showExposure);
          }}
        >
          <div className="flex items-center justify-between">
            Exposure <input checked={showExposure} readOnly type="checkbox" />
          </div>
        </DropdownMenu.Item>
        <DropdownMenu.Separator className="h-px bg-gray-200 my-1" />
        <DropdownMenu.Item className={itemClass} onSelect={handleLogout}>
          Sign Out
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  </DropdownMenu.Root>
);
};

export default UserDropdown;
